import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { BreadcrumbItem } from 'ComponentLibrary'
import type { PdfValidatorCopy, ValXmlExtractorCopy } from 'ComponentLibrary/src/playground'
import type { PlaygroundToolHeaderCopy } from 'ComponentLibrary/src/playground/shared/layout/PlaygroundToolHeader'

import { STATIC_APP_PATHS } from 'ClientPortal/src/router/constants'

type PlaygroundTranslations = {
    dashboardBreadcrumbRoot: BreadcrumbItem
    toolHeaderCopy: PlaygroundToolHeaderCopy
    valXmlExtractorCopy: ValXmlExtractorCopy
    pdfValidatorCopy: PdfValidatorCopy
}

const GENERAL_SECTION_TRANSLATION_KEYS: Record<string, string> = {
    'Gutachter & Besichtigung': 'appraiserInspection',
    Bewertungsergebnisse: 'valuationResults',
    'Kosten & Abzüge': 'costsDeductions',
    'Adresse & Eigentümer': 'addressOwner',
    'Weitere Kennzahlen': 'otherKeyFigures',
}

const RENTAL_SECTION_TRANSLATION_KEYS: Record<string, string> = {
    Archiv: 'archive',
    Gewerbe: 'businessGeneral',
    Gastronomie: 'gastronomy',
    Hotel: 'hotel',
    Innenstellplätze: 'indoorParking',
    Freizeit: 'leisure',
    Büro: 'office',
    Außenstellplätze: 'outdoorParking',
    Wohnen: 'residential',
    Handel: 'retail',
    Sozial: 'social',
    Lager: 'storage',
    'Sonstige Nutzung': 'otherUsage',
}

const VAL_XML_RENTAL_FIELD_SUFFIXES = [
    'EstimatedAnnualRentForLetNumbers',
    'EstimatedAnnualRentForVacantNumbers',
    'EstimatedAnnualRentForLetArea',
    'EstimatedAnnualRentForVacantArea',
    'EstimatedAnnualRentLet',
    'EstimatedAnnualRentVacant',
    'ContractualAnnualRent',
    'VacantNumbers',
    'LetNumbers',
    'VacantArea',
    'LetArea',
] as const

type TranslationMap = Record<string, string>

const readTranslationMap = (value: unknown): TranslationMap => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {}
    }
    return value as TranslationMap
}

const normaliseValXmlTranslationPath = (path: string): string =>
    path
        .trim()
        .replace(/^ValXML\./i, '')
        .replace(/(^|[.])ns1:/gi, '$1')

const parseRentalValXmlPath = (path: string): { usageKey: string; fieldKey: string } | null => {
    const normalizedPath = normaliseValXmlTranslationPath(path)
    const rentalSuffix = normalizedPath.split('RentalSituation', 2)[1]
    if (!rentalSuffix) {
        return null
    }
    const fieldKey = VAL_XML_RENTAL_FIELD_SUFFIXES.find((suffix) => rentalSuffix.endsWith(suffix))
    if (!fieldKey) {
        return null
    }
    const usageKey = rentalSuffix.slice(0, -fieldKey.length)
    return usageKey ? { usageKey, fieldKey } : null
}

export const usePlaygroundTranslations = (): PlaygroundTranslations => {
    const { t, i18n } = useTranslation('pages', { keyPrefix: 'playground' })
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tAiDataPrivacyInfo } = useTranslation('terminology', { keyPrefix: 'common.aiDataPrivacyInfo' })

    return useMemo(() => {
        const valXml = tCommon('valXml')
        const plausibility = tCommon('plausibility')
        const report = tCommon('report')
        const unknown = tCommon('unknown')
        const marketRent = tCommon('marketRent')
        const pdfValidatorStartLabel = t('pdfValidator.questions.start', { plausibility })

        const header = {
            defaultBreadcrumbTitle: t('shared.header.defaultBreadcrumbTitle'),
            betaLabel: t('shared.header.betaLabel'),
            learnMore: t('shared.header.learnMore'),
            close: t('shared.header.close'),
            modalTitle: (title: string) => t('shared.header.modalTitle', { title }),
            dataPrivacyInfo: {
                tooltip: tAiDataPrivacyInfo('tooltip'),
                label: tAiDataPrivacyInfo('label'),
                modalTitle: tAiDataPrivacyInfo('modalTitle'),
                closeButtonLabel: tAiDataPrivacyInfo('closeButtonLabel'),
                items: {
                    confidentialData: tAiDataPrivacyInfo('items.confidentialData'),
                    storageDuration: tAiDataPrivacyInfo('items.storageDuration'),
                    euProcessing: tAiDataPrivacyInfo('items.euProcessing'),
                    noModelTraining: tAiDataPrivacyInfo('items.noModelTraining'),
                    supportPrefix: tAiDataPrivacyInfo('items.supportPrefix'),
                    supportLinkLabel: tAiDataPrivacyInfo('items.supportLinkLabel'),
                },
            },
        }

        const pdfPreview = {
            searchLabel: t('shared.pdfPreview.searchLabel'),
            searchPlaceholder: t('shared.pdfPreview.searchPlaceholder'),
            previousResult: t('shared.pdfPreview.previousResult'),
            nextResult: t('shared.pdfPreview.nextResult'),
            noResults: t('shared.pdfPreview.noResults'),
            searchResultMessage: (current: number, total: number, page: number) =>
                t('shared.pdfPreview.searchResultMessage', { current, total, page }),
            pdfLoadError: t('shared.pdfPreview.pdfLoadError'),
            previousPage: t('shared.pdfPreview.previousPage'),
            nextPage: t('shared.pdfPreview.nextPage'),
            page: t('shared.pdfPreview.page'),
            zoomOut: t('shared.pdfPreview.zoomOut'),
            zoomIn: t('shared.pdfPreview.zoomIn'),
        }

        const upload = {
            fileSizeErrorMessage: t('shared.upload.fileSizeErrorMessage'),
            replacePdf: t('shared.upload.replacePdf'),
            uploadPdf: t('shared.upload.uploadPdf'),
            uploadValuationPdf: t('shared.upload.uploadValuationPdf', { report }),
            cancel: t('shared.upload.cancel'),
            valuationPdf: t('shared.upload.valuationPdf', { report }),
            dropDescription: t('shared.upload.dropDescription'),
            emptyTitle: t('shared.upload.emptyTitle', { report }),
            sampleButton: t('shared.upload.sampleButton'),
            sampleTooltip: t('shared.upload.sampleTooltip'),
            uploadTooltip: t('shared.upload.uploadTooltip', { report }),
            restartAnalysis: t('shared.upload.restartAnalysis'),
            removeFile: t('shared.upload.removeFile'),
        }

        const translateGeneralSection = (label: string): string => {
            const numericSuffix = label.match(/\s+\d+$/)?.[0] ?? ''
            const baseLabel = numericSuffix ? label.slice(0, -numericSuffix.length) : label
            const key = GENERAL_SECTION_TRANSLATION_KEYS[baseLabel]
            return key ? `${t(`valXmlExtractor.kpiSection.generalSections.${key}`)}${numericSuffix}` : label
        }

        const translateRentalSection = (label: string): string => {
            const key = RENTAL_SECTION_TRANSLATION_KEYS[label]
            return key ? t(`valXmlExtractor.kpiSection.rentalSections.${key}`) : label
        }

        const translateExtractionSection = (label: string): string => {
            const rentalMatch = label.match(/^RentalSituation\s+(\d+)$/)
            if (rentalMatch?.[1]) {
                return `RentalSituation ${rentalMatch[1]}`
            }
            return translateGeneralSection(label)
        }

        const valXmlFieldLabels = readTranslationMap(
            t('valXmlExtractor.kpiSection.valXmlFields.labels', { returnObjects: true, defaultValue: {} })
        )
        const valXmlFieldDescriptions = readTranslationMap(
            t('valXmlExtractor.kpiSection.valXmlFields.descriptions', { returnObjects: true, defaultValue: {} })
        )
        const valXmlRentalUsageLabels = readTranslationMap(
            t('valXmlExtractor.kpiSection.valXmlFields.rentalUsageLabels', { returnObjects: true, defaultValue: {} })
        )
        const valXmlRentalFieldLabels = readTranslationMap(
            t('valXmlExtractor.kpiSection.valXmlFields.rentalFieldLabels', { returnObjects: true, defaultValue: {} })
        )

        const getValXmlMapValue = (map: TranslationMap, path: string): string | undefined => {
            const normalizedPath = normaliseValXmlTranslationPath(path)
            return map[normalizedPath] ?? map[path]
        }

        const getRentalFieldLabel = (path: string): string | undefined => {
            const parsedPath = parseRentalValXmlPath(path)
            if (!parsedPath) {
                return undefined
            }
            const usage = valXmlRentalUsageLabels[parsedPath.usageKey]
            const field = valXmlRentalFieldLabels[parsedPath.fieldKey]
            return usage && field ? `${usage} - ${field}` : undefined
        }

        const getRentalFieldDescription = (path: string): string | undefined => {
            const parsedPath = parseRentalValXmlPath(path)
            if (!parsedPath) {
                return undefined
            }
            const usage = valXmlRentalUsageLabels[parsedPath.usageKey]
            const field = valXmlRentalFieldLabels[parsedPath.fieldKey]
            return usage && field ? t('valXmlExtractor.kpiSection.valXmlFields.rentalDescription', { usage, field }) : undefined
        }

        const valXmlFields = {
            label: (path: string, fallback: string): string =>
                getValXmlMapValue(valXmlFieldLabels, path) ?? getRentalFieldLabel(path) ?? fallback,
            description: (path: string, fallback?: string): string | undefined =>
                getValXmlMapValue(valXmlFieldDescriptions, path) ?? getRentalFieldDescription(path) ?? fallback,
            rentalUsageLabel: (usage: string): string => valXmlRentalUsageLabels[usage] ?? usage,
        }

        const addModal = {
            title: t('valXmlExtractor.kpiSection.addModal.title'),
            add: t('valXmlExtractor.kpiSection.addModal.add'),
            cancel: t('valXmlExtractor.kpiSection.addModal.cancel'),
            fieldLabel: t('valXmlExtractor.kpiSection.addModal.fieldLabel'),
            fieldPlaceholder: t('valXmlExtractor.kpiSection.addModal.fieldPlaceholder'),
            valueLabel: t('valXmlExtractor.kpiSection.addModal.valueLabel'),
            fallbackRentalUsage: t('valXmlExtractor.kpiSection.addModal.fallbackRentalUsage'),
            generalGroupLabel: t('valXmlExtractor.kpiSection.addModal.generalGroupLabel'),
            rentalGroupLabel: (usage: string) => t('valXmlExtractor.kpiSection.addModal.rentalGroupLabel', { usage }),
            expectedType: (type: string) => t('valXmlExtractor.kpiSection.addModal.expectedType', { type }),
        }

        const valXmlExtractorCopy: ValXmlExtractorCopy = {
            toolHeader: header,
            pdfPreview,
            upload,
            kpiSection: {
                addModal,
                valXmlFields,
                validation: {
                    missingValXmlField: t('valXmlExtractor.kpiSection.validation.missingValXmlField', { valXml }),
                    missingValue: t('valXmlExtractor.kpiSection.validation.missingValue'),
                    kpiAdded: t('valXmlExtractor.kpiSection.validation.kpiAdded'),
                },
                labels: {
                    review: t('valXmlExtractor.kpiSection.labels.review'),
                    page: t('valXmlExtractor.kpiSection.labels.page'),
                    confidence: t('valXmlExtractor.kpiSection.labels.confidence'),
                    manualConfidence: t('valXmlExtractor.kpiSection.labels.manualConfidence'),
                    valXmlPath: t('valXmlExtractor.kpiSection.labels.valXmlPath', { valXml }),
                    explanation: t('valXmlExtractor.kpiSection.labels.explanation'),
                    notes: t('valXmlExtractor.kpiSection.labels.notes'),
                    codeInterpreterUsed: t('valXmlExtractor.kpiSection.labels.codeInterpreterUsed'),
                    title: t('valXmlExtractor.kpiSection.labels.title'),
                    valXml: t('valXmlExtractor.kpiSection.labels.valXml', { valXml }),
                },
                actions: {
                    removeKpi: t('valXmlExtractor.kpiSection.actions.removeKpi'),
                    copyValue: t('valXmlExtractor.kpiSection.actions.copyValue'),
                    acceptProposal: t('valXmlExtractor.kpiSection.actions.acceptProposal'),
                    rejectProposal: t('valXmlExtractor.kpiSection.actions.rejectProposal'),
                    addKpi: t('valXmlExtractor.kpiSection.actions.addKpi'),
                    addMissingKpi: t('valXmlExtractor.kpiSection.actions.addMissingKpi'),
                    reviewTooltip: t('valXmlExtractor.kpiSection.actions.reviewTooltip'),
                    reviewRunning: t('valXmlExtractor.kpiSection.actions.reviewRunning'),
                    review: t('valXmlExtractor.kpiSection.actions.review'),
                },
                placeholders: {
                    value: t('valXmlExtractor.kpiSection.placeholders.value'),
                },
                feedback: {
                    valueCopied: t('valXmlExtractor.kpiSection.feedback.valueCopied'),
                    valXmlUnavailable: t('valXmlExtractor.kpiSection.feedback.valXmlUnavailable', { valXml }),
                    valXmlExportStarted: t('valXmlExtractor.kpiSection.feedback.valXmlExportStarted', { valXml }),
                    extractingInitial: t('valXmlExtractor.kpiSection.feedback.extractingInitial'),
                    extractingMore: t('valXmlExtractor.kpiSection.feedback.extractingMore'),
                },
                booleanLabels: {
                    true: t('valXmlExtractor.kpiSection.booleanLabels.true'),
                    false: t('valXmlExtractor.kpiSection.booleanLabels.false'),
                },
                generalSectionLabel: translateGeneralSection,
                rentalSectionLabel: translateRentalSection,
            },
            secondaryCheck: {
                invalidJson: t('valXmlExtractor.secondaryCheck.invalidJson'),
                invalidFormat: t('valXmlExtractor.secondaryCheck.invalidFormat'),
                missingValueReviewNote: t('valXmlExtractor.secondaryCheck.missingValueReviewNote'),
                summary: {
                    newEntries: (count: number) => t('valXmlExtractor.secondaryCheck.newEntries', { count }),
                    valueChanges: (count: number) => t('valXmlExtractor.secondaryCheck.valueChanges', { count }),
                    proposedChanges: (count: number) => t('valXmlExtractor.secondaryCheck.proposedChanges', { count }),
                    confidenceReduced: (count: number) => t('valXmlExtractor.secondaryCheck.confidenceReduced', { count }),
                    join: (parts: string[]) => parts.join(t('valXmlExtractor.secondaryCheck.joinSeparator')),
                    noChanges: t('valXmlExtractor.secondaryCheck.noChanges'),
                },
            },
            title: t('valXmlExtractor.title', { valXml }),
            infoText: t('valXmlExtractor.infoText', { valXml }),
            infoModalItems: t('valXmlExtractor.infoModalItems', { returnObjects: true, valXml }) as string[],
            emptyDescription: t('valXmlExtractor.emptyDescription', { valXml }),
            aiResponse: {
                language: t('valXmlExtractor.aiResponse.language'),
                marketRentTerm: marketRent,
                requiredImportDefaultNote: t('valXmlExtractor.aiResponse.requiredImportDefaultNote'),
            },
            progress: {
                defaultExtraction: t('valXmlExtractor.progress.defaultExtraction'),
                extractingSection: (section: string) => t('valXmlExtractor.progress.extractingSection', { section }),
                sectionLabel: translateExtractionSection,
            },
            errors: {
                missingReviewData: t('valXmlExtractor.errors.missingReviewData'),
                missingReviewKpis: t('valXmlExtractor.errors.missingReviewKpis'),
                noReadableText: t('valXmlExtractor.errors.noReadableText'),
                pdfProcessing: t('valXmlExtractor.errors.pdfProcessing'),
                pdfProviderFit: t('valXmlExtractor.errors.pdfProviderFit'),
                samplePdf: t('valXmlExtractor.errors.samplePdf'),
                valXmlBuild: t('valXmlExtractor.errors.valXmlBuild', { valXml }),
                authExpired: t('valXmlExtractor.errors.authExpired'),
                forbidden: t('valXmlExtractor.errors.forbidden'),
                endpointMissing: t('valXmlExtractor.errors.endpointMissing'),
                rateLimit: t('valXmlExtractor.errors.rateLimit'),
                server: t('valXmlExtractor.errors.server'),
                missingToken: t('valXmlExtractor.errors.missingToken'),
                responseMissingJson: t('valXmlExtractor.errors.responseMissingJson'),
                responseInvalidJson: t('valXmlExtractor.errors.responseInvalidJson'),
                responseInvalidFormat: t('valXmlExtractor.errors.responseInvalidFormat'),
                unknown: t('valXmlExtractor.errors.unknown'),
                genericAiError: (code?: number | string, bodyMessage?: string) =>
                    bodyMessage
                        ? t('valXmlExtractor.errors.genericAiErrorWithMessage', { code: code ?? unknown, message: bodyMessage })
                        : t('valXmlExtractor.errors.genericAiError', { code: code ?? unknown }),
            },
        }

        const pdfValidatorCopy: PdfValidatorCopy = {
            locale: i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'de',
            toolHeader: header,
            pdfPreview,
            upload: {
                ...upload,
                sampleTooltip: t('pdfValidator.upload.sampleTooltip'),
            },
            title: t('pdfValidator.title', { plausibility }),
            infoText: t('pdfValidator.infoText', { plausibility }),
            infoModalItems: t('pdfValidator.infoModalItems', {
                returnObjects: true,
                startLabel: pdfValidatorStartLabel,
            }) as string[],
            emptyDescription: t('pdfValidator.emptyDescription', { plausibility }),
            answerLabels: {
                yes: t('pdfValidator.answerLabels.yes'),
                no: t('pdfValidator.answerLabels.no'),
                textAnswer: t('pdfValidator.answerLabels.textAnswer'),
                unknown: t('pdfValidator.answerLabels.unknown'),
            },
            errors: {
                noClearAnswer: t('pdfValidator.errors.noClearAnswer'),
                retryValidation: t('pdfValidator.errors.retryValidation', { plausibility, startLabel: pdfValidatorStartLabel }),
                noReadableText: t('pdfValidator.errors.noReadableText'),
                tableOfContentsLoadingFailed: t('pdfValidator.errors.tableOfContentsLoadingFailed', { plausibility }),
                pdfProcessing: t('pdfValidator.errors.pdfProcessing'),
                pdfProviderFit: t('pdfValidator.errors.pdfProviderFit'),
                samplePdf: t('pdfValidator.errors.samplePdf'),
            },
            progress: {
                tableOfContents: t('pdfValidator.progress.tableOfContents'),
            },
            standardChecks: {
                title: t('pdfValidator.standardChecks.title'),
                tooltip: t('pdfValidator.standardChecks.tooltip'),
                questions: t('pdfValidator.standardChecks.questions', {
                    returnObjects: true,
                }) as PdfValidatorCopy['standardChecks']['questions'],
            },
            tableOfContents: {
                title: t('pdfValidator.tableOfContents.title'),
                loading: t('pdfValidator.tableOfContents.loading'),
                empty: t('pdfValidator.tableOfContents.empty'),
                pending: t('pdfValidator.tableOfContents.pending'),
            },
            notes: {
                title: t('pdfValidator.notes.title'),
                copy: t('pdfValidator.notes.copy'),
                placeholder: t('pdfValidator.notes.placeholder'),
                textEditor: {
                    toolbar: {
                        textStyle: t('pdfValidator.notes.textEditor.toolbar.textStyle'),
                        bold: t('pdfValidator.notes.textEditor.toolbar.bold'),
                        italic: t('pdfValidator.notes.textEditor.toolbar.italic'),
                        bulletedList: t('pdfValidator.notes.textEditor.toolbar.bulletedList'),
                        numberedList: t('pdfValidator.notes.textEditor.toolbar.numberedList'),
                        headingOptions: {
                            paragraph: t('pdfValidator.notes.textEditor.toolbar.headingOptions.paragraph'),
                            h2: t('pdfValidator.notes.textEditor.toolbar.headingOptions.h2'),
                            h3: t('pdfValidator.notes.textEditor.toolbar.headingOptions.h3'),
                            h4: t('pdfValidator.notes.textEditor.toolbar.headingOptions.h4'),
                            h5: t('pdfValidator.notes.textEditor.toolbar.headingOptions.h5'),
                        },
                    },
                },
            },
            questions: {
                title: t('pdfValidator.questions.title'),
                queued: t('pdfValidator.questions.queued'),
                start: pdfValidatorStartLabel,
                startInfo: t('pdfValidator.questions.startInfo', { plausibility }),
                newPlaceholder: t('pdfValidator.questions.newPlaceholder'),
                create: t('pdfValidator.questions.create'),
                emptyWithPersisted: t('pdfValidator.questions.emptyWithPersisted', { plausibility }),
                emptyWithoutPersisted: t('pdfValidator.questions.emptyWithoutPersisted'),
                generateExamples: t('pdfValidator.questions.generateExamples'),
                loadPast: t('pdfValidator.questions.loadPast'),
                save: t('pdfValidator.questions.save'),
                cancelEdit: t('pdfValidator.questions.cancelEdit'),
                edit: t('pdfValidator.questions.edit'),
                delete: t('pdfValidator.questions.delete'),
                examples: t('pdfValidator.questions.examples', { returnObjects: true }) as string[],
            },
        }

        return {
            dashboardBreadcrumbRoot: { title: tCommon('overview'), link: STATIC_APP_PATHS.dashboard },
            toolHeaderCopy: header,
            valXmlExtractorCopy,
            pdfValidatorCopy,
        }
    }, [i18n.resolvedLanguage, t, tAiDataPrivacyInfo, tCommon])
}
